import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, ListChecks, Receipt, Users, Settings, FileBarChart2,
  Menu, X, LogOut, ChevronRight, Shield, SlidersHorizontal,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";

const NAV_BASE = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testId: "nav-tasks" },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, permissions, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMobile, setOpenMobile] = useState(false);

  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  const isSuperAdmin = user?.role === "super_admin";
  const canFinance =
    isAdmin || permissions?.can_track_finance || permissions?.can_view_finance;

  const brand = settings?.brand || { name: "Koinonia Church", letter: "K", tagline: "Operations" };

  const nav = [
    ...NAV_BASE,
    ...(canFinance ? [{ to: "/expenses", label: "Financials", icon: Receipt, testId: "nav-expenses" }] : []),
    ...(isAdmin ? [{ to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" }] : []),
    ...(isAdmin ? [{ to: "/users", label: "Team", icon: Users, testId: "nav-users" }] : []),
    ...(isAdmin ? [{ to: "/roles", label: "Roles", icon: Shield, testId: "nav-roles" }] : []),
    ...(isSuperAdmin ? [{ to: "/settings", label: "Settings", icon: SlidersHorizontal, testId: "nav-settings" }] : []),
  ];

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 flex">
      {/* Sidebar — desktop only (hidden < lg) */}
      <aside
        className={`hidden lg:flex z-40 inset-y-0 left-0 w-[260px] bg-white border-r border-neutral-200 flex-col`}
      >
        <div className="px-3 pb-5 flex items-center gap-2.5">
          <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-neutral-950 text-white grid place-items-center font-bold text-base shrink-0">
              {brand.letter || "K"}
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-semibold text-[15px] truncate text-[#1C1C1E]">{brand.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8E8E93] mt-0.5 truncate">{brand.tagline}</div>
            </div>
          </Link>
        </div>

        <nav className="space-y-0.5">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 h-10 rounded-xl text-[14px] font-medium transition-all ${
                  isActive
                    ? "bg-white text-[#1C1C1E] shadow-[0_1px_3px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]"
                    : "text-[#52525B] hover:bg-black/[0.04]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={18} strokeWidth={isActive ? 2.2 : 1.7} color={isActive ? "#E07A1F" : "#71717A"} />
                  <span>{item.label}</span>
                  {isActive && <div className="ml-auto h-1.5 w-1.5 rounded-full bg-[#E07A1F]" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <div className="px-3 pt-3 mt-3 border-t border-black/[0.06] flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-200 to-rose-300 grid place-items-center font-bold text-amber-900">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate text-[#1C1C1E]" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#8E8E93] truncate">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/[0.04] text-[#71717A]"
              aria-label="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Desktop Header (sticky); mobile uses iOS-style large title inside page content */}
        <header className="hidden lg:flex sticky top-0 z-30 h-16 bg-[#F4F5F7]/85 backdrop-blur-xl items-center justify-between px-10">
          <div className="flex items-center gap-3 min-w-0">
            <Breadcrumbs path={location.pathname} brandName={brand.name} />
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>

        {/* Mobile top bar — minimal, status-bar-ish */}
        <div className="lg:hidden flex items-center justify-between px-5 pt-3 pb-1">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-md bg-neutral-950 text-white grid place-items-center font-display font-semibold text-sm shrink-0">
              {brand.letter || "K"}
            </div>
            <div className="font-display font-semibold text-[14px] truncate max-w-[160px] text-[#1C1C1E]">{brand.name}</div>
          </Link>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 p-0.5 rounded-full hover:bg-neutral-100"
            data-testid="mobile-profile-button"
            aria-label="Sign out"
            title="Sign out"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-neutral-200" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-neutral-100 grid place-items-center font-semibold text-neutral-700">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
          </button>
        </div>

        <main className="flex-1 px-0 lg:px-10 py-0 lg:py-6 lg:max-w-[1500px] w-full mx-auto">
          {/* Desktop title block (mobile pages render their own LargeTitle) */}
          {(title || subtitle) && (
            <div className="hidden lg:block mb-8 rise px-0">
              {subtitle && <div className="eyebrow mb-2">{subtitle}</div>}
              {title && (
                <h1 className="text-[36px] font-bold tracking-tight leading-tight text-[#1C1C1E]">
                  {title}
                </h1>
              )}
            </div>
          )}
          {children}
        </main>

        {/* Mobile bottom tab bar */}
        <MobileTabBar nav={nav} />
      </div>
    </div>
  );
}

function MobileTabBar({ nav }) {
  const items = nav.slice(0, 5);
  const location = useLocation();
  return (
    <nav
      data-testid="mobile-tab-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/85 backdrop-blur-2xl border-t border-black/[0.06] pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch pt-1.5 pb-1">
        {items.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`tab-${item.testId}`}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5"
              style={{ color: isActive ? "#E07A1F" : "#8E8E93" }}
            >
              <item.icon size={22} strokeWidth={isActive ? 2.4 : 1.8} />
              <span className="text-[10px] font-semibold">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function Breadcrumbs({ path, brandName }) {
  const parts = path.split("/").filter(Boolean);
  return (
    <div className="flex items-center gap-1.5 text-[13px] text-neutral-600 min-w-0">
      <span className="overline">{brandName}</span>
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-1.5 min-w-0">
          <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
          <span className="capitalize truncate text-neutral-900 font-medium">
            {decodeURIComponent(p)}
          </span>
        </span>
      ))}
    </div>
  );
}

export function PageSection({ title, description, actions, children }) {
  return (
    <section className="rise">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div>
          {title && <div className="overline mb-1">{title}</div>}
          {description && <p className="text-sm text-neutral-600">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
